import { useEffect, useState } from "react";

// @mui
import { AUTH_JWT_TOKEN, AUTH_REFRESH_TOKEN, Avatar, Box, IconButton, Menu, MenuItem, Stack, Typography, Divider, Tooltip } from "@compass/core";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppAuthStatus } from "redux/reducers/appSlice";
import { RootState } from "redux/store";
import { User } from "generated/openApi";
import ApiService from "services/apiService";
import { userManualPageRoute } from "pages/UserManual/UserManualPage";
import { userPageRoute } from "pages/users/UserPage/UserPage";
import Person2Icon from '@mui/icons-material/Person2';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import PaletteIcon from '@mui/icons-material/Palette';
import { setAvatarColor } from "redux/reducers/appSlice";


const AVATAR_COLORS = [
  '#FFB3BA',
  '#FFDFBA',
  '#F7DC6F',
  '#BAFFC9',
  '#BAE1FF',
  '#D4BAFF',
  '#FFBAF3',
  '#FFD4BA',
  '#C9FFE5',
  '#E0BBE4',
  '#FFDFD3',
  '#B4E7CE',
];

const DEFAULT_AVATAR_COLOR = '#BAE1FF'; // Pastel blue


export const AppTopBarUserMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appUserFullName = useSelector((state: RootState) => state.app.userFullName);
  const appUserMembership = useSelector((state: RootState) => state.app.userMembership);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const avatarColor = useSelector(
    (state: RootState) => state.app.avatarColor
  ) || DEFAULT_AVATAR_COLOR;



  useEffect(() => {
    if (appUserMembership) {
      ApiService.users.getByUserMembership(appUserMembership).then((response) => {
        setUserData(response);

        dispatch(setAvatarColor(response.avatarColor || DEFAULT_AVATAR_COLOR));
      });
    }
  }, [appUserMembership, dispatch]);


  const logout = () => {
    const refreshToken: string | null = localStorage.getItem(AUTH_REFRESH_TOKEN);
    localStorage.removeItem(AUTH_JWT_TOKEN);
    localStorage.removeItem(AUTH_REFRESH_TOKEN);

    if (refreshToken) {
      ApiService.auth.logout(refreshToken);
    }

    dispatch(setAppAuthStatus("NOT_AUTHENTICATED")); // tell redux we are authenticated
  };

  const userManual = () => {
    navigate(userManualPageRoute.getPath());
    setUserMenuAnchorEl(null);
  }

  const userProfile = () => {
    if (appUserMembership) {
      navigate(userPageRoute.getPath({ membership: appUserMembership, crudMode: "view" }));
    }
    setUserMenuAnchorEl(null);
  }

  const handleClose = () => {
    setUserMenuAnchorEl(null);
    setShowColorPicker(false);
  };

  const handleColorSelect = async (color: string) => {
    dispatch(setAvatarColor(color));
    setShowColorPicker(false);
    if (userData) {
      try {
        await ApiService.users.saveUpdateSelf({
          ...userData,
          avatarColor: color,
        });
        setUserData({
          ...userData,
          avatarColor: color,
        });
      } catch (error) {
        console.error("Failed to save avatar color:", error);
        dispatch(setAvatarColor(userData.avatarColor || DEFAULT_AVATAR_COLOR));
      }
    }
  };

  const toggleColorPicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowColorPicker(!showColorPicker);
  };


  return (
    <>
      <IconButton onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}>
        {appUserFullName && <Avatar string={appUserFullName} bgcolor={avatarColor} />}
      </IconButton>
      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              borderRadius: 3,
              mt: 1.5,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              backgroundColor: '#f5f9ff',

            }
          }
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, textAlign: 'center', pr: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {userData?.email || ''}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ mt: -0.5, mr: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        {/* Avatar section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
          <Box sx={{ position: 'relative', width: 80, height: 80 }}>
            {appUserFullName && <Avatar string={appUserFullName} size="large" bgcolor={avatarColor} />}
            <IconButton
              size="small"
              onClick={toggleColorPicker}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' }
              }}
            >
              <Tooltip title="Change avatar color" placement="right" arrow>
                <PaletteIcon fontSize="small" />
              </Tooltip>
            </IconButton>
          </Box>
          {/* Color Picker */}
          {showColorPicker && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 2,
                boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
                Choose avatar color
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(6, 1fr)',
                  gap: 1,
                  maxWidth: 240
                }}
              >
                {AVATAR_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: color,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      border: avatarColor === color ? '3px solid' : '2px solid',
                      borderColor: avatarColor === color ? 'primary.main' : 'divider',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: 2
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Typography variant="h6" sx={{ mt: 2 }}>
            Hi, {userData?.firstName || 'User'}!
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ px: 1 }}>
          <MenuItem onClick={userProfile} sx={{ my: 0.5, borderRadius: 1, py: 1.5 }}>
            <Stack direction="row" spacing="sm" alignItems="center">
              <Person2Icon />
              <Typography>Manage User Account</Typography>
            </Stack>
          </MenuItem>

          <MenuItem onClick={userManual} sx={{ my: 0.5, borderRadius: 1, py: 1.5 }}>
            <Stack direction="row" spacing="sm" alignItems="center">
              <AutoStoriesIcon />
              <Typography>User Manual</Typography>
            </Stack>
          </MenuItem>
        </Box>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={logout} sx={{ mx: 1, my: 0.5, mb: 1, borderRadius: 1 }}>
          <Stack direction="row" spacing="sm" alignItems="center">
            <LogoutIcon />
            <Typography>Sign out</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
};
